import { useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { httpsCallable } from 'firebase/functions'
import { functions } from '@/lib/firebase'
import { useAuth } from '@/hooks/useAuth'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { DeviceConfigDialog } from '@/components/DeviceConfigDialog'
import { InstallCommandDialog } from '@/components/InstallCommandDialog'
import { DeviceLogs } from '@/components/DeviceLogs'
import { useTelegramLink } from '@/hooks/useTelegramLink'
import { TelegramLinkDialog } from '@/components/TelegramLinkDialog'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { ArrowLeft, Settings, LogOut, Camera, MapPin, MessageCircle, Terminal } from 'lucide-react'
import { formatDateTime } from '@/lib/dateTime'
import type { Device } from '@/types'

export function DevicePage() {
  const { deviceId } = useParams<{ deviceId: string }>()
  const navigate = useNavigate()
  const { t } = useTranslation()
  const { user, logout } = useAuth()
  const { linked: telegramLinked } = useTelegramLink()
  const [device, setDevice] = useState<Device | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [configOpen, setConfigOpen] = useState(false)
  const [installOpen, setInstallOpen] = useState(false)
  const [telegramDialogOpen, setTelegramDialogOpen] = useState(false)

  const loadDevice = async () => {
    if (!deviceId) return

    try {
      setLoading(true)
      const getUserDevices = httpsCallable(functions, 'getUserDevices')
      const result = await getUserDevices({})
      const data = result.data as { success: boolean; devices: Device[] }

      if (data.success) {
        const found = data.devices.find((d) => d.id === deviceId)
        if (found) {
          setDevice(found)
        } else {
          setError(t('devices.deviceNotFound'))
        }
      }
    } catch (err) {
      console.error('Failed to load device:', err)
      setError(t('devices.failedToLoad'))
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    loadDevice()
  }, [deviceId])

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'online':
        return <span className="badge-online">{t('devices.status.online')}</span>
      case 'idle':
        return <span className="badge-idle">{t('devices.status.idle')}</span>
      default:
        return <span className="badge-offline">{t('devices.status.offline')}</span>
    }
  }

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-paper">
        <div className="h-8 w-8 animate-spin rounded-full border-b-2 border-ink"></div>
      </div>
    )
  }

  if (error || !device) {
    return (
      <div className="min-h-screen bg-paper">
        <nav className="border-b border-rule bg-paper-2/60 backdrop-blur-sm">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex justify-between h-16 items-center">
              <Button variant="ghost" onClick={() => navigate('/')} className="px-0">
                <ArrowLeft className="h-4 w-4" strokeWidth={1.6} />
                {t('devices.backToDevices')}
              </Button>
            </div>
          </div>
        </nav>
        <main className="max-w-7xl mx-auto py-10 px-4 sm:px-6 lg:px-8">
          <Card>
            <CardContent className="py-10">
              <Alert variant="destructive">
                <AlertDescription>{error || t('devices.deviceNotFound')}</AlertDescription>
              </Alert>
            </CardContent>
          </Card>
        </main>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-paper">
      {/* Auvers nav: paper-2 strip with hairline rule, museum-label logo on left. */}
      <nav className="border-b border-rule bg-paper-2/60 backdrop-blur-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col sm:flex-row sm:justify-between py-3 sm:h-[68px] gap-3 sm:gap-0 sm:items-center">
            <div className="flex items-center gap-5">
              <Button variant="ghost" onClick={() => navigate('/')} className="px-0 self-start sm:self-auto">
                <ArrowLeft className="h-4 w-4" strokeWidth={1.6} />
                <span className="text-[14px]">{t('devices.backToDevices')}</span>
              </Button>
              <a href="/" className="auvers-logo hidden md:inline-flex">
                AUVERS
                <span className="product">Sentry Manager</span>
              </a>
            </div>

            {/* User Actions */}
            <div className="flex items-center gap-2 sm:gap-3 justify-end">
              <span className="hidden md:inline-block max-w-[200px] truncate font-mono text-[12px] tracking-wider text-ink-soft">
                {user?.email}
              </span>

              {/* Telegram Link Indicator — tonal pill, gold dot when linked. */}
              <button
                onClick={() => setTelegramDialogOpen(true)}
                className={`grid h-9 w-9 place-items-center rounded-pill border shrink-0 transition-colors ${
                  telegramLinked
                    ? 'border-gold-soft bg-paper-3 text-ink hover:bg-[#fbf3dd]'
                    : 'border-rule bg-transparent text-ink-soft hover:bg-paper-2'
                }`}
                title={telegramLinked ? t('telegram.linked') : t('telegram.linkForAlerts')}
              >
                <MessageCircle className="h-4 w-4" strokeWidth={1.6} />
              </button>

              {/* Logout */}
              <Button variant="ghost" size="sm" onClick={logout} className="shrink-0">
                <LogOut className="h-4 w-4 sm:mr-1.5" />
                <span className="hidden sm:inline">{t('common.logout')}</span>
              </Button>
            </div>
          </div>
        </div>
      </nav>

      {/* Main content */}
      <main className="max-w-7xl mx-auto py-10 px-4 sm:px-6 lg:px-8">
        {/* Device Header — eyebrow, serif name, gold rule, mono metadata grid. */}
        <div className="mb-8 flex flex-wrap items-end justify-between gap-6">
          <div>
            <span className="eyebrow">{t('tabs.devices')} · {device.id.slice(0, 8)}</span>
            <h1 className="mt-2 font-serif text-[40px] font-medium leading-[1.05] tracking-[-0.015em] text-ink">
              {device.name}
            </h1>
            <hr className="gold-rule mt-3" />
            <div className="mt-3 inline-flex items-center gap-2 text-[14px] text-ink-soft">
              <MapPin className="h-4 w-4" strokeWidth={1.6} />
              <span>{device.location || t('devices.noLocation')}</span>
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-3">
            {getStatusBadge(device.status)}
            {device.isGroupAdmin && (
              <>
                <Button variant="ghost" onClick={() => setInstallOpen(true)}>
                  <Terminal className="h-4 w-4" strokeWidth={1.6} />
                  {t('install.showCommand')}
                </Button>
                <Button onClick={() => setConfigOpen(true)}>
                  <Settings className="h-4 w-4" strokeWidth={1.6} />
                  {t('devices.configure')}
                </Button>
              </>
            )}
          </div>
        </div>

        {/* Device metadata — instrument readout in mono. */}
        <Card className="mb-8">
          <CardContent className="p-8">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-x-6 gap-y-5">
              <div>
                <span className="eyebrow text-[10px]">{t('devices.lastSeen')}</span>
                <p className="mt-2 font-mono text-[13px] tracking-wider text-ink">{formatDateTime(device.lastSeen)}</p>
              </div>
              <div>
                <span className="eyebrow text-[10px]">{t('devices.totalDetections')}</span>
                <p className="mt-2 font-mono text-[13px] tracking-wider text-ink">{device.totalDetections}</p>
              </div>
              <div>
                <span className="eyebrow text-[10px]">{t('devices.cameras')}</span>
                <p className="mt-2 inline-flex items-center gap-1.5 font-mono text-[13px] tracking-wider text-ink">
                  <Camera className="h-4 w-4 text-gold" strokeWidth={1.6} />
                  {t('devices.configured', { count: device.cameras?.length || 0 })}
                </p>
              </div>
              <div>
                <span className="eyebrow text-[10px]">{t('devices.statusLabel')}</span>
                <p className="mt-2 font-mono text-[13px] tracking-wider text-ink">
                  {device.isActive ? t('devices.active') : t('devices.inactive')}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Logs */}
        <Card>
          <CardHeader>
            <CardTitle>{t('deviceLogs.title')}</CardTitle>
            <hr className="gold-rule" />
          </CardHeader>
          <CardContent>
            <DeviceLogs groupId={device.groupId} deviceId={device.id} />
          </CardContent>
        </Card>
      </main>

      {/* Config Dialog */}
      <DeviceConfigDialog
        open={configOpen}
        onOpenChange={setConfigOpen}
        device={device}
        onDeviceUpdated={loadDevice}
      />

      <TelegramLinkDialog
        open={telegramDialogOpen}
        onOpenChange={setTelegramDialogOpen}
      />

      {device.apiKey && (
        <InstallCommandDialog
          open={installOpen}
          onOpenChange={setInstallOpen}
          device={device}
        />
      )}
    </div>
  )
}
